import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { 
  X, 
  Calendar as CalendarIcon, 
  Clock,
  BookOpen,
  Users,
  MapPin,
  Upload,
  Plus,
  Trash2,
  Target,
  Smartphone,
  LayoutTemplate,
  ArrowRight,
  FileText 
} from 'lucide-react';
import { getWhatsappTemplates } from '../../api/services/noticeApi';
import { useData } from '../../contexts/DataContext';
import SUBJECT_API from '../../api/services/subjectApi';

const CalendarEventForm = ({ event, onClose, onDelete, onSave, initialDate }) => {
  const { classes: apiClasses, currentAcademicYear } = useData();

  // Derive initial form data from props using useMemo to avoid re-computation
  const initialFormData = useMemo(() => {
    if (event) {
      const newStartDate = event.startDate ? new Date(event.startDate).toISOString().split('T')[0] : '';
      const newEndDate = event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '';
      
      return {
        title: event.title || '',
        type: event.type || 'event',
        description: event.description || '',
        startDate: newStartDate,
        endDate: newEndDate,
        examSchedule: event.examSchedule || [],
        applicableClasses: event.applicableClasses || [],
        applicableBranches: event.applicableBranches || [],
        attachments: event.attachments || [],
        isRecurring: event.isRecurring || false,
        recurrencePattern: event.recurrencePattern || 'none',
        createAlsoAsNotice: false,
        autoSendNotice: false
      };
    }
    
    return {
      title: '',
      type: 'event',
      description: '',
      startDate: initialDate ? new Date(initialDate).toISOString().split('T')[0] : '',
      endDate: initialDate ? new Date(initialDate).toISOString().split('T')[0] : '',
      examSchedule: [],
      applicableClasses: [],
      applicableBranches: [],
      attachments: [],
      isRecurring: false,
      recurrencePattern: 'none',
      createAlsoAsNotice: false,
      autoSendNotice: false
    };
  }, [event, initialDate]);

  const [formData, setFormData] = useState(initialFormData);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState(null);
  const [whatsappParams, setWhatsappParams] = useState({});
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  const [newExam, setNewExam] = useState({
    classId: '',
    subjectId: '',
    subjectName: '',
    date: '',
    startTime: '',
    endTime: '',
    room: ''
  });

  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const [showExamForm, setShowExamForm] = useState(false);

  // Synchronize form data when event or initialDate props change
  // This is a legitimate use case for setState in useEffect because we're synchronizing
  // internal form state with external props (controlled by parent component)
  React.useEffect(() => {
    setFormData(initialFormData);
    fetchTemplates();
  }, [initialFormData]);

  React.useEffect(() => {
    if (newExam.classId) {
      fetchSubjects(newExam.classId);
    } else {
      setAvailableSubjects([]);
    }
  }, [newExam.classId]);

  const fetchSubjects = async (unitId) => {
    setLoadingSubjects(true);
    try {
      const res = await SUBJECT_API.getSubjectsByClass(unitId, currentAcademicYear);
      if (res.success) {
        setAvailableSubjects(res.data || []);
      }
    } catch (err) {
      toast.error("Failed to load subjects for this class");
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplate(true);
    try {
      const res = await getWhatsappTemplates();
      if (res.success && Array.isArray(res.data)) {
        setWhatsappTemplates(res.data);
      }
    } catch (err) {
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleWhatsappTemplateSelect = (templateName) => {
    const template = whatsappTemplates.find(t => t.templateName === templateName);
    setSelectedWhatsappTemplate(template || null);
    
    const initialParams = {};
    const params = template?.parameterFields || template?.masterTemplate?.bodyParams || template?.bodyParams || [];
    
    params.forEach(p => {
      const pName = typeof p === 'string' ? p : p.name;
      const lowerName = pName.toLowerCase();
      if (lowerName !== 'student_name' && lowerName !== 'name') {
        initialParams[pName] = '';
      }
    });
    
    setWhatsappParams(initialParams);

    if (template) {
       setFormData(prev => ({ 
         ...prev, 
         title: template.event || prev.title 
       }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleExamInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'subjectId') {
      const selectedSub = availableSubjects.find(s => s._id === value);
      setNewExam(prev => ({
        ...prev,
        [name]: value,
        subjectName: selectedSub ? selectedSub.subjectName : ''
      }));
    } else {
      setNewExam(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const addExamSchedule = () => {
    if (newExam.subjectName && newExam.date && newExam.startTime && newExam.endTime) {
      const exam = {
        subjectId: newExam.subjectId || Date.now().toString(),
        subjectName: newExam.subjectName,
        date: new Date(newExam.date),
        startTime: newExam.startTime,
        endTime: newExam.endTime,
        room: newExam.room,
        unitId: newExam.classId
      };
      
      setFormData(prev => ({
        ...prev,
        examSchedule: [...prev.examSchedule, exam],
        // Also add class to applicableClasses if not already there
        applicableClasses: newExam.classId && !prev.applicableClasses.includes(newExam.classId)
          ? [...prev.applicableClasses, newExam.classId]
          : prev.applicableClasses
      }));
      
      setNewExam({
        classId: '',
        subjectId: '',
        subjectName: '',
        date: '',
        startTime: '',
        endTime: '',
        room: ''
      });
      setShowExamForm(false);
    } else {
      toast.error("Please fill all required fields for the exam");
    }
  };

  const removeExamSchedule = (index) => {
    setFormData(prev => ({
      ...prev,
      examSchedule: prev.examSchedule.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    
    // Validate required fields
    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }

    const templateData = selectedWhatsappTemplate?.masterTemplate || selectedWhatsappTemplate?.templateId || selectedWhatsappTemplate;
    const params = selectedWhatsappTemplate?.parameterFields || templateData?.bodyParams || [];

    const eventData = {
      ...formData,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      examSchedule: formData.examSchedule.map(exam => ({
        ...exam,
        subjectId: exam.subjectId || Date.now().toString()
      })),
      whatsappTemplate: selectedWhatsappTemplate?.templateName,
      whatsappParams: params.map(p => {
        const pName = typeof p === 'string' ? p : p.name;
        const lowerName = pName.toLowerCase();
        if (lowerName === 'student_name' || lowerName === 'name') {
          return '{{student_name}}';
        }
        return whatsappParams[pName];
      }) || []
    };

    onSave(eventData);
  };

  const combinedEventTypes = useMemo(() => {
    const staticTypes = [
      { value: 'holiday', label: 'Holiday', category: 'General' },
      { value: 'exam', label: 'Exam', category: 'Academic' },
      { value: 'event', label: 'Event', category: 'General' },
      { value: 'meeting', label: 'Meeting', category: 'Staff' },
      { value: 'orientation', label: 'Orientation', category: 'Academic' },
      { value: 'workshop', label: 'Workshop', category: 'Staff' },
      { value: 'result_day', label: 'Result Day', category: 'Academic' },
      // { value: 'other', label: 'Other', category: 'General' }
    ];
    
    const templateTypes = whatsappTemplates.map(t => ({
      value: `notice:${t.templateName}`,
      label: `Broadcast: ${t.event || t.templateName}`,
      category: 'Official Broadcast',
      template: t
    }));

    return [...staticTypes, ...templateTypes];
  }, [whatsappTemplates]);

  const handleTypeChange = (value) => {
    if (value.startsWith('notice:')) {
      const templateName = value.split(':')[1];
      const template = whatsappTemplates.find(t => t.templateName === templateName);
      
      // Infer type from template event name if possible
      let inferredType = 'event';
      const eventName = (template?.event || '').toLowerCase();
      if (eventName.includes('exam')) inferredType = 'exam';
      else if (eventName.includes('holiday')) inferredType = 'holiday';
      else if (eventName.includes('meeting')) inferredType = 'meeting';
      else if (eventName.includes('result')) inferredType = 'result_day';

      setSelectedWhatsappTemplate(template || null);
      setFormData(prev => ({ 
        ...prev, 
        type: inferredType,
        createAlsoAsNotice: true,
        autoSendNotice: true,
        title: template?.event || prev.title 
      }));

      const initialParams = {};
      const params = template?.parameterFields || template?.masterTemplate?.bodyParams || template?.bodyParams || [];
      
      params.forEach(p => {
        const pName = typeof p === 'string' ? p : p.name;
        const lowerName = pName.toLowerCase();
        if (lowerName !== 'student_name' && lowerName !== 'name') {
          initialParams[pName] = '';
        }
      });
      setWhatsappParams(initialParams);
    } else {
      setSelectedWhatsappTemplate(null);
      setFormData(prev => ({ 
        ...prev, 
        type: value,
        createAlsoAsNotice: false,
        autoSendNotice: false
      }));
    }
  };

  const isBroadcast = !!selectedWhatsappTemplate;
  const labels = {
    title: isBroadcast ? 'Notice Title *' : 'Event Title *',
    description: isBroadcast ? 'Notice Content / Description' : 'Event Description',
    basicInfo: isBroadcast ? 'Broadcast Information' : 'Basic Information',
    modalTitle: isBroadcast ? (event ? 'Edit Broadcast' : 'Create Official Broadcast') : (event ? 'Edit Event' : 'Add New Event')
  };

  const recurrencePatterns = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl shadow-lg rounded bg-white flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-sm font-bold text-gray-900">
            {labels.modalTitle}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Information */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-900 flex items-center space-x-1.5 uppercase tracking-wider">
                <CalendarIcon className="h-3.5 w-3.5" />
                <span>{labels.basicInfo}</span>
              </h4>
              
              {!isBroadcast && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{labels.title}</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-2 px-2 input-field font-bold"
                      placeholder="e.g., Final Exams, Diwali Holiday"
                      required
                    />
                  </div>
                )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Event Type *</label>
                <div className="relative">
                  <select
                    name="type"
                    value={selectedWhatsappTemplate ? `notice:${selectedWhatsappTemplate.templateName}` : formData.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-2 px-2 input-field appearance-none pr-8"
                    required
                    disabled={loadingTemplate}
                  >
                    <option value="" disabled>Select Type...</option>
                    {/* Standard Events Group */}
                    <optgroup label="Calendar Events">
                      {combinedEventTypes.filter(t => t.category !== 'Official Broadcast').map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </optgroup>
                    
                    {/* Broadcast Templates Group */}
                    <optgroup label="Official Broadcasts">
                      {combinedEventTypes.filter(t => t.category === 'Official Broadcast').map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <ArrowRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
                  {loadingTemplate && (
                    <div className="absolute right-8 top-1/2 -translate-y-1/2">
                      <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

                {!isBroadcast && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{labels.description}</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-2 px-2 input-field font-semibold"
                      rows="3"
                      placeholder="Optional details..."
                    />
                  </div>
                )}
            </div>

            {/* Date & Time */}
            {/* Date & Time */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-900 flex items-center space-x-1.5 uppercase tracking-wider">
                <Clock className="h-3.5 w-3.5" />
                <span>Date & Time</span>
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className={isBroadcast ? 'col-span-2' : ''}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2 input-field font-bold"
                    required
                  />
                </div>

                {!isBroadcast && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date *</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2 input-field"
                      required
                    />
                  </div>
                )}
              </div>

              {!isBroadcast && (
                <>
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isRecurring"
                        checked={formData.isRecurring}
                        onChange={handleInputChange}
                        className="h-3.5 w-3.5 focus:ring-indigo-500 border-gray-300 rounded"
                        style={{ color: 'var(--primary-color, #4f46e5)' }}
                      />
                      <span className="text-xs text-gray-700">Recurring Event</span>
                    </label>
                  </div>

                  {formData.isRecurring && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Recurrence Pattern</label>
                      <select
                        name="recurrencePattern"
                        value={formData.recurrencePattern}
                        onChange={handleInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2 input-field"
                      >
                        {recurrencePatterns.map(pattern => (
                          <option key={pattern.value} value={pattern.value}>
                            {pattern.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Exam Schedule */}
          {formData.type === 'exam' && !isBroadcast && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-gray-900 flex items-center space-x-1.5 uppercase tracking-wider">
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Exam Schedule</span>
                </h4>
                <button
                  type="button"
                  onClick={() => setShowExamForm(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add Subject</span>
                </button>
              </div>

              {/* Existing Exam Schedule */}
              {formData.examSchedule.length > 0 && (
                <div className="space-y-2">
                  {formData.examSchedule.map((exam, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 text-xs">
                          <span className="font-bold text-gray-900">{exam.subjectName}</span>
                          <span className="text-gray-500">
                            {new Date(exam.date).toLocaleDateString()}
                          </span>
                          <span className="text-gray-500">
                            {exam.startTime} - {exam.endTime}
                          </span>
                          {exam.room && (
                            <span className="text-gray-500 flex items-center space-x-1">
                              <MapPin className="h-3 w-3" />
                              {exam.room}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExamSchedule(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Exam Form */}
              {showExamForm && (
                <div className="p-3 border border-gray-200 rounded bg-white">
                  <h5 className="text-xs font-bold text-gray-900 mb-2">Add New Subject</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">Class *</label>
                      <select
                        name="classId"
                        value={newExam.classId}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        required
                      >
                        <option value="">Select Class</option>
                        {apiClasses.map(cls => (
                          <option key={cls._id} value={cls._id}>{cls.unitName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">Subject *</label>
                      <select
                        name="subjectId"
                        value={newExam.subjectId}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        required
                        disabled={!newExam.classId || loadingSubjects}
                      >
                        <option value="">{newExam.classId ? (loadingSubjects ? 'Loading...' : 'Select Subject') : 'Select Class First'}</option>
                        {availableSubjects.map(sub => (
                          <option key={sub._id} value={sub._id}>{sub.subjectName}</option>
                        ))}
                        {/* Fallback for manual entry if subject not in list, but user wants subject selection from API */}
                        {newExam.classId && !loadingSubjects && availableSubjects.length === 0 && (
                          <option value="none" disabled>No subjects found for this class</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        type="date"
                        name="date"
                        value={newExam.date}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">Start Time *</label>
                      <input
                        type="time"
                        name="startTime"
                        value={newExam.startTime}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">End Time *</label>
                      <input
                        type="time"
                        name="endTime"
                        value={newExam.endTime}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-1">Room</label>
                      <input
                        type="text"
                        name="room"
                        value={newExam.room}
                        onChange={handleExamInputChange}
                        className="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5 px-2"
                        placeholder="Hall A"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end space-x-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setShowExamForm(false)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={addExamSchedule}
                      className="px-3 py-1.5 text-white rounded text-xs font-medium btn-primary"
                    >
                      Add Subject
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attachments & Communication */}
          <div className={`grid grid-cols-1 ${!isBroadcast ? 'md:grid-cols-2' : ''} gap-6`}>
            {!isBroadcast && (
              <div className="space-y-3 animate-in fade-in duration-300">
                <h4 className="text-xs font-bold text-gray-900 flex items-center space-x-1.5 uppercase tracking-wider">
                  <Upload className="h-3.5 w-3.5" />
                  <span>Attachments</span>
                </h4>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">File Attachments</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="block w-full text-xs text-slate-500
                      file:mr-4 file:py-1.5 file:px-3
                      file:rounded file:border-0
                      file:text-xs file:font-semibold
                      file:bg-indigo-50"
                    style={{ '--tw-file-bg': 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', '--tw-file-text': 'var(--primary-color, #4f46e5)' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files).map(file => file.name);
                      setFormData(prev => ({
                        ...prev,
                        attachments: [...prev.attachments, ...files]
                      }));
                    }}
                  />
                  
                  {formData.attachments.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {formData.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-xs text-gray-700">{attachment}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                attachments: prev.attachments.filter((_, i) => i !== index)
                              }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {selectedWhatsappTemplate ? (
                <div className="animate-in slide-in-from-right-4 duration-300">
                  <h4 className="text-xs font-bold text-gray-900 flex items-center space-x-1.5 uppercase tracking-wider mb-3">
                    <FileText className="h-3.5 w-3.5" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                    <span>Communication Details</span>
                  </h4>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {(selectedWhatsappTemplate.parameterFields || selectedWhatsappTemplate.masterTemplate?.bodyParams || selectedWhatsappTemplate.bodyParams || [])
                        .filter(param => {
                          const paramName = (typeof param === 'string' ? param : param.name).toLowerCase();
                          return paramName !== 'student_name' && paramName !== 'name';
                        })
                        .map((param) => {
                          const paramName = typeof param === 'string' ? param : param.name;
                          const label = typeof param === 'string' 
                            ? paramName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                            : param.label;
                          
                          return (
                            <div key={paramName}>
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">
                                {label}
                              </label>
                              <input
                                value={whatsappParams[paramName] || ''}
                                onChange={(e) => setWhatsappParams(v => ({ ...v, [paramName]: e.target.value }))}
                                className="w-full bg-gray-50 border border-gray-100 rounded-lg py-2 px-3 text-xs font-bold text-gray-800 focus:bg-white focus:border-indigo-500 transition-all outline-none"
                                placeholder={`Enter ${label.toLowerCase()}...`}
                                required
                              />
                            </div>
                          );
                        })}
                    </div>
                    
                    <div className="pt-2 flex items-center gap-2">
                       <span className="flex-1 h-px bg-gray-100" />
                       <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Automatic Broadcast Active</span>
                       <span className="flex-1 h-px bg-gray-100" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                   <LayoutTemplate size={32} className="text-gray-300 mb-2" />
                   <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight"> Standard Event Flow </p>
                   <p className="text-[9px] text-gray-400 mt-1">Select a "Broadcast" type to enable <br/> official notifications.</p>
                </div>
              )}
            </div>
          </div>

          </form>
          </div>
          {/* Form Actions - Sticky Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-white/80 backdrop-blur-md rounded-b-2xl flex-shrink-0 z-30 sticky bottom-0">
            {event && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex items-center px-4 py-2 text-red-600 bg-white hover:bg-red-50 rounded-xl border border-red-200 text-xs font-bold transition-all shadow-sm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Record
              </button>
            )}
            
            <div className={`flex items-center space-x-3 ${!event || !onDelete ? 'ml-auto w-full justify-end' : ''}`}>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 text-xs font-bold transition-all shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                type="button"
                className="px-8 py-2.5 text-white rounded-xl text-xs font-black btn-primary shadow-lg shadow-indigo-100 transform active:scale-95 transition-all"
              >
                {event ? 'Update Record' : 'Create Record'}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default CalendarEventForm;
